import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../api";
import { ADMIN_LOGIN_PATH, ADMIN_DASHBOARD_PATH, ADMIN_MOBILE_PATH } from "./Adminpaths";

const CATEGORIES = ["vegetables", "flowers"];
const emptyForm = { name: "", price: "", stock: "", quantity: "", image: null, subcategory: "Fresh vegetables" };
const SUBCATEGORY_OPTIONS = ["Root vegetables", "Cruciferous vegetables", "Fresh vegetables", "Leafy vegetables", "Gourds"];

// How long to wait after the admin stops typing before auto-saving.
const AUTOSAVE_DELAY_MS = 800;

const getEnglishName = (name) => {
  if (typeof name === "string") return name;
  if (name && typeof name === "object") return name.en || Object.values(name)[0] || "";
  return "";
};

/* ---------------------------------------------------------------------- */
/* Styles — plain CSS, scoped with an "aia-" prefix, embedded in the file */
/* so the page renders correctly with no build-tool (e.g. Tailwind) setup */
/* ---------------------------------------------------------------------- */

const styles = `
.aia-app { display: flex; height: 100vh; background: #f8fafc; font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #0f172a; }

/* Sidebar — kept identical across admin pages for quick, familiar navigation */
.aia-sidebar { width: 240px; flex-shrink: 0; background: #0f172a; color: #e2e8f0; display: flex; flex-direction: column; }
.aia-sidebar__brand { padding: 20px; font-size: 20px; font-weight: 700; letter-spacing: -0.01em; border-bottom: 1px solid rgba(255,255,255,0.08); }
.aia-sidebar__nav { flex: 1; padding: 16px 12px; display: flex; flex-direction: column; gap: 4px; }
.aia-sidebar__link { display: block; padding: 10px 14px; border-radius: 6px; font-size: 14px; color: #cbd5e1; text-decoration: none; background: transparent; border: none; text-align: left; cursor: pointer; width: 100%; }
.aia-sidebar__link:hover { background: rgba(255,255,255,0.06); color: #fff; }
.aia-sidebar__link--active { background: #2874f0; color: #fff; font-weight: 600; }
.aia-sidebar__footer { padding: 12px; border-top: 1px solid rgba(255,255,255,0.08); }
.aia-sidebar__logout { display: flex; align-items: center; gap: 8px; width: 100%; padding: 10px 14px; border-radius: 6px; font-size: 14px; color: #fca5a5; background: transparent; border: none; cursor: pointer; text-align: left; }
.aia-sidebar__logout:hover { background: rgba(248,113,113,0.12); }

/* Main column */
.aia-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.aia-header { padding: 22px 32px; background: #fff; border-bottom: 1px solid #e2e8f0; }
.aia-header h1 { margin: 0; font-size: 19px; font-weight: 700; }
.aia-header p { margin: 4px 0 0; font-size: 13px; color: #64748b; }
.aia-content { flex: 1; overflow-y: auto; padding: 28px 32px 48px; }

/* Quick price panel */
.aia-panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 20px; margin-bottom: 32px; }
.aia-panel__title { margin: 0 0 4px; font-size: 15px; font-weight: 700; }
.aia-panel__hint { margin: 0 0 16px; font-size: 13px; color: #64748b; }
.aia-table-wrap { overflow-x: auto; }
.aia-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.aia-table thead tr { text-align: left; background: #f1f5f9; }
.aia-table th { padding: 9px 10px; font-weight: 600; color: #475569; border-bottom: 1px solid #e2e8f0; }
.aia-table td { padding: 9px 10px; border-bottom: 1px solid #f1f5f9; }
.aia-table tr.aia-row--out { opacity: 0.55; }
.aia-price-input { width: 90px; padding: 6px 8px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; }
.aia-price-input:focus { outline: none; border-color: #2874f0; }

/* Grouped pack-size variants inside one quick-price-table row */
.aia-variant-list { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; }
.aia-variant { display: flex; align-items: center; gap: 6px; padding: 6px 10px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; }
.aia-variant__qty { font-size: 12px; font-weight: 600; color: #475569; white-space: nowrap; }
.aia-variant--out { opacity: 0.6; }
.aia-variant--disabled { opacity: 0.6; background: #f1f5f9; }

/* Status pill, shared by cards and the quick-price table */
.aia-status { font-size: 12px; font-weight: 600; }
.aia-status--pending { color: #b8860b; }
.aia-status--saving { color: #2874f0; }
.aia-status--saved { color: #0c831f; }
.aia-status--error { color: #e11d48; }

/* Category sections */
.aia-section { margin-bottom: 40px; }
.aia-section__title { font-size: 16px; font-weight: 700; text-transform: capitalize; margin: 0 0 14px; }
.aia-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 18px; }

/* Toolbar — search + category filter chips, sits above the sections */
.aia-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 18px; margin-bottom: 24px; }
.aia-search { position: relative; flex: 1; min-width: 220px; }
.aia-search__icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }
.aia-search__input { width: 100%; padding: 9px 12px 9px 34px; border-radius: 8px; border: 1px solid #cbd5e1; font-size: 13.5px; box-sizing: border-box; font-family: inherit; }
.aia-search__input:focus { outline: none; border-color: #2874f0; }
.aia-chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
.aia-chip { border: 1px solid #cbd5e1; background: #fff; color: #475569; padding: 7px 14px; border-radius: 999px; font-size: 12.5px; font-weight: 600; cursor: pointer; text-transform: capitalize; white-space: nowrap; }
.aia-chip:hover { background: #f1f5f9; }
.aia-chip--active { background: #2874f0; border-color: #2874f0; color: #fff; }
.aia-empty-note { font-size: 13px; color: #94a3b8; padding: 6px 0 18px; }

/* Item card */
.aia-card { display: flex; flex-direction: column; gap: 8px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; position: relative; }
.aia-card--out { opacity: 0.6; }
.aia-card--disabled { opacity: 0.6; background: #f8fafc; }
.aia-card__badge { position: absolute; top: 8px; right: 8px; background: #dc2626; color: #fff; font-size: 10.5px; font-weight: 700; padding: 3px 7px; border-radius: 4px; z-index: 1; }
.aia-card__badge--disabled { background: #64748b; }
.aia-card__toggle { width: 100%; background: #fff; color: #92400e; border: 1px solid #fde68a; padding: 7px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; }
.aia-card__toggle:hover { background: #fffbeb; }
.aia-card__toggle:disabled { cursor: not-allowed; opacity: 0.5; }
.aia-card__toggle--enable { color: #0c831f; border-color: #bbf7d0; }
.aia-card__toggle--enable:hover { background: #f0fdf4; }
.aia-card__image { width: 100%; height: 116px; object-fit: cover; border-radius: 6px; background: #f1f5f9; }
.aia-card__image-empty { width: 100%; height: 116px; border-radius: 6px; background: #f1f5f9; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #94a3b8; }
.aia-card__image-label { font-size: 12px; color: #2874f0; cursor: pointer; font-weight: 500; }
.aia-card__image-label input { display: none; }
.aia-field-label { font-size: 11.5px; color: #64748b; font-weight: 500; display: flex; flex-direction: column; gap: 3px; }
.aia-input, .aia-select, .aia-textarea { width: 100%; padding: 6px 8px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; box-sizing: border-box; font-family: inherit; background: #fff; }
.aia-input:focus, .aia-select:focus, .aia-textarea:focus { outline: none; border-color: #2874f0; }
.aia-input:disabled, .aia-select:disabled, .aia-textarea:disabled { background: #f8fafc; color: #94a3b8; }
.aia-card__name { font-size: 13.5px; font-weight: 600; }
.aia-card__status-row { display: flex; justify-content: center; padding: 2px 0; min-height: 16px; }
.aia-card__delete { width: 100%; background: #fff; color: #dc2626; border: 1px solid #fecaca; padding: 7px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; }
.aia-card__delete:hover { background: #fef2f2; }
.aia-card__delete:disabled { cursor: not-allowed; opacity: 0.5; }

/* Add-item card */
.aia-add-card { border: 1.5px dashed #cbd5e1; background: #fff; border-radius: 10px; padding: 16px; width: 230px; display: flex; flex-direction: column; gap: 9px; }
.aia-add-card__title { font-size: 13px; font-weight: 700; color: #334155; margin: 0 0 2px; }
.aia-btn { border: none; border-radius: 6px; padding: 9px 14px; font-size: 13px; font-weight: 700; cursor: pointer; }
.aia-btn--primary { background: #2874f0; color: #fff; }
.aia-btn--primary:hover { background: #1e5fce; }
.aia-btn--primary:disabled { background: #cbd5e1; cursor: not-allowed; }

/* Tablet: sidebar stays, just tightens up */
@media (max-width: 900px) {
  .aia-sidebar { width: 190px; }
}

/* Phone: sidebar collapses into a top bar so content gets full width */
@media (max-width: 640px) {
  .aia-app { flex-direction: column; height: auto; min-height: 100vh; }
  .aia-sidebar { width: 100%; flex-direction: row; align-items: center; }
  .aia-sidebar__brand { padding: 14px 16px; border-bottom: none; font-size: 16px; white-space: nowrap; }
  .aia-sidebar__nav { flex-direction: row; overflow-x: auto; gap: 4px; padding: 10px 8px; }
  .aia-sidebar__link { white-space: nowrap; padding: 8px 12px; }
  .aia-sidebar__footer { border-top: none; padding: 10px 12px 10px 0; }
  .aia-sidebar__logout { width: auto; padding: 8px 12px; white-space: nowrap; }

  .aia-header { padding: 14px 16px; }
  .aia-content { padding: 14px 16px 32px; }

  .aia-panel { padding: 16px; }
  .aia-panel__hint { margin-bottom: 12px; }
  .aia-table th, .aia-table td { padding: 8px 6px; }

  .aia-section { margin-bottom: 28px; }
  .aia-grid { grid-template-columns: repeat(3, 1fr); gap: 6px; }
  .aia-card { padding: 6px; gap: 5px; }
  .aia-card__image, .aia-card__image-empty { height: 60px; }
  .aia-card__badge { font-size: 8px; padding: 2px 4px; top: 4px; right: 4px; }
  .aia-card__image-label { font-size: 9.5px; }
  .aia-field-label { font-size: 9px; gap: 2px; }
  .aia-input, .aia-select { padding: 4px 5px; font-size: 10.5px; }
  .aia-card__name { font-size: 10.5px; }
  .aia-card__delete { padding: 5px; font-size: 9.5px; }
  .aia-card__status-row { min-height: 12px; }
  .aia-add-card { width: 100%; box-sizing: border-box; }

  .aia-toolbar { padding: 12px 14px; }
  .aia-search { min-width: 100%; }
  .aia-chip-row { width: 100%; overflow-x: auto; flex-wrap: nowrap; padding-bottom: 2px; }

  .aia-btn { padding: 10px 14px; }
}
`;

// Small status pill shared by the item cards and the quick price table so
// "auto-saving" always looks and behaves the same way everywhere.
function AutoSaveStatus({ status }) {
  const labels = { pending: "Unsaved…", saving: "Saving…", saved: "✓ Saved", error: "⚠ Fix value" };
  const text = labels[status];
  if (!text) return null;
  return <span className={`aia-status aia-status--${status}`}>{text}</span>;
}

function ItemEditCard({ category, item, onSave, onDelete, onToggle }) {
  const buildDraft = (src) => ({
    name: getEnglishName(src.name),
    price: src.price ?? "",
    stock: src.stock ?? "",
    subcategory: src.subcategory || "Fresh vegetables",
    quantity: src.quantity || "",
  });

  const [form, setForm] = useState(() => buildDraft(item));
  const [status, setStatus] = useState("idle"); // idle | pending | saving | saved | error
  const saveTimer = useRef(null);
  const latestForm = useRef(form);
  latestForm.current = form;

  // Only resync from the server copy when there's nothing pending/in-flight,
  // so we never clobber an edit the admin is still typing (or saving).
  useEffect(() => {
    if (status === "idle") setForm(buildDraft(item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.name, item.price, item.stock, item.subcategory, item.quantity]);

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const flushSave = async (draft, opts = {}) => {
    if (draft.name.trim() === "" || draft.price === "" || draft.stock === "") {
      setStatus("error");
      return;
    }
    const priceNum = Number(draft.price);
    const stockNum = Number(draft.stock);
    if (Number.isNaN(priceNum) || priceNum < 0 || Number.isNaN(stockNum) || stockNum < 0) {
      setStatus("error");
      return;
    }

    setStatus("saving");
    try {
      await onSave(category, item.id, draft.name.trim(), priceNum, stockNum, draft.subcategory, draft.quantity, opts.imageFile);
      setStatus("saved");
      setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (err) {
      console.error("Item save failed:", err.response?.status, err.response?.data || err.message);
      setStatus("error"); // keep the draft so nothing typed is lost
    }
  };

  const scheduleSave = (nextForm) => {
    setStatus("pending");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushSave(nextForm), AUTOSAVE_DELAY_MS);
  };

  const change = (field, value) => {
    const next = { ...form, [field]: value };
    setForm(next);
    scheduleSave(next);
  };

  // Save right away on blur too, so tabbing to the next field doesn't
  // leave the admin waiting out the debounce delay.
  const flushNow = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    flushSave(latestForm.current);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    flushSave(latestForm.current, { imageFile: file });
  };

  const saving = status === "saving";
  const outOfStock = Number(item.stock) <= 0;
  const isDisabled = item.enabled === false;
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    try {
      await onToggle(category, item.id, isDisabled);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className={`aia-card ${outOfStock ? "aia-card--out" : ""} ${isDisabled ? "aia-card--disabled" : ""}`}>
      {isDisabled ? (
        <span className="aia-card__badge aia-card__badge--disabled">Disabled</span>
      ) : (
        outOfStock && <span className="aia-card__badge">Out of stock</span>
      )}

      {item.image ? (
        <img
          src={item.image.startsWith("http") ? item.image : `${API_URL}/${item.image}`}
          alt={getEnglishName(item.name)}
          className="aia-card__image"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      ) : (
        <div className="aia-card__image-empty">No image</div>
      )}

      <label className="aia-card__image-label">
        Change image
        <input type="file" accept="image/*" disabled={saving} onChange={handleImageChange} />
      </label>

      <input
        className="aia-input aia-card__name"
        value={form.name}
        placeholder="Item Name"
        disabled={saving}
        onChange={(e) => change("name", e.target.value)}
        onBlur={flushNow}
      />

      <label className="aia-field-label">
        Subcategory
        <select className="aia-select" value={form.subcategory} disabled={saving} onChange={(e) => change("subcategory", e.target.value)}>
          {SUBCATEGORY_OPTIONS.map((sub) => (<option key={sub} value={sub}>{sub}</option>))}
        </select>
      </label>

      <label className="aia-field-label">
        Quantity
        <input className="aia-input" type="text" placeholder="e.g. 500 grms, 1 kg" value={form.quantity} disabled={saving} onChange={(e) => change("quantity", e.target.value)} onBlur={flushNow} />
      </label>

      <label className="aia-field-label">
        Price
        <input className="aia-input" type="number" min="0" step="0.01" value={form.price} disabled={saving} onChange={(e) => change("price", e.target.value)} onBlur={flushNow} />
      </label>

      <label className="aia-field-label">
        Stock
        <input className="aia-input" type="number" min="0" value={form.stock} disabled={saving} onChange={(e) => change("stock", e.target.value)} onBlur={flushNow} />
      </label>

      <div className="aia-card__status-row">
        <AutoSaveStatus status={status} />
      </div>

      <button
        className={`aia-card__toggle ${isDisabled ? "aia-card__toggle--enable" : ""}`}
        onClick={handleToggle}
        disabled={saving || toggling}
      >
        {toggling ? "Updating…" : isDisabled ? "Enable item" : "Disable item"}
      </button>

      <button className="aia-card__delete" onClick={() => onDelete(category, item.id)} disabled={saving}>
        Delete item
      </button>
    </div>
  );
}

// One pack-size variant's price/stock inputs (e.g. the "1kg" or "500 grms"
// version of an item). Several of these sit together inside one grouped
// row when an item name has more than one pack size.
function VariantPriceStock({ category, item, onSave }) {
  const [price, setPrice] = useState(item.price ?? "");
  const [stock, setStock] = useState(item.stock ?? "");
  const [status, setStatus] = useState("idle");
  const saveTimer = useRef(null);

  // Always hold the latest typed values, so a save triggered by one field
  // (blur, debounce) never sends a stale value for the other field.
  const latest = useRef({ price, stock });
  latest.current = { price, stock };

  useEffect(() => {
    if (status === "idle") setPrice(item.price ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.price]);

  useEffect(() => {
    if (status === "idle") setStock(item.stock ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.stock]);

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const flushSave = async (vals) => {
    const priceNum = Number(vals.price);
    const stockNum = Number(vals.stock);
    if (
      vals.price === "" || Number.isNaN(priceNum) || priceNum < 0 ||
      vals.stock === "" || Number.isNaN(stockNum) || stockNum < 0
    ) {
      setStatus("error");
      return;
    }
    setStatus("saving");
    try {
      await onSave(
        category,
        item.id,
        getEnglishName(item.name),
        priceNum,
        stockNum,
        item.subcategory,
        item.quantity
      );
      setStatus("saved");
      setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (err) {
      console.error("Price/stock save failed:", err.response?.status, err.response?.data || err.message);
      setStatus("error");
    }
  };

  const scheduleSave = () => {
    setStatus("pending");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushSave(latest.current), AUTOSAVE_DELAY_MS);
  };

  const handlePriceChange = (e) => {
    setPrice(e.target.value);
    scheduleSave();
  };

  const handleStockChange = (e) => {
    setStock(e.target.value);
    scheduleSave();
  };

  const handleBlur = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    flushSave(latest.current);
  };

  const outOfStock = Number(item.stock) <= 0;
  const isDisabled = item.enabled === false;

  return (
    <div className={`aia-variant ${outOfStock ? "aia-variant--out" : ""} ${isDisabled ? "aia-variant--disabled" : ""}`}>
      <span className="aia-variant__qty">{item.quantity || "—"}{isDisabled ? " · Disabled" : ""}</span>
      <input
        className="aia-price-input"
        type="number"
        min="0"
        step="0.01"
        value={price}
        disabled={status === "saving"}
        onChange={handlePriceChange}
        onBlur={handleBlur}
        title="Price"
      />
      <input
        className="aia-price-input"
        type="number"
        min="0"
        value={stock}
        disabled={status === "saving"}
        onChange={handleStockChange}
        onBlur={handleBlur}
        title="Stock"
      />
      <AutoSaveStatus status={status} />
    </div>
  );
}

// One row per distinct item name. When the same item is stocked in more
// than one pack size (e.g. 1kg and 500 grms are saved as two separate
// items with the same name), all of its pack sizes are listed together
// here instead of as separate, disconnected rows.
function ItemGroupRow({ category, name, variants, onSave }) {
  return (
    <tr>
      <td>{name}</td>
      <td style={{ textTransform: "capitalize", color: "#64748b" }}>{category}</td>
      <td>
        <div className="aia-variant-list">
          {variants.map((item) => (
            <VariantPriceStock key={item.id} category={category} item={item} onSave={onSave} />
          ))}
        </div>
      </td>
    </tr>
  );
}

// Flat table of every item across every category, for the "I just need to
// bump 30 prices before the shop opens" workflow — no cards, no save
// buttons, just type the new price and move to the next row. Items that
// share a name (different pack sizes of the same product) are grouped
// into a single row so their prices/stocks sit side by side.
// Groups items whose names are the same product but were typed slightly
// differently at different times - extra/missing spaces, spacing inside
// parentheses, stray punctuation, etc. Comparing only letters and numbers
// (ignoring case, spaces, and punctuation entirely) makes the match much
// more forgiving than a plain trim+lowercase.
const normalizeForGrouping = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

function QuickPriceTable({ itemsByCategory, onSave }) {
  const rows = CATEGORIES.flatMap((cat) => (itemsByCategory[cat] || []).map((item) => ({ category: cat, item })));
  if (rows.length === 0) return null;

  const groups = [];
  const groupIndexByKey = new Map();
  rows.forEach(({ category, item }) => {
    const key = `${category}::${normalizeForGrouping(getEnglishName(item.name))}`;
    if (!groupIndexByKey.has(key)) {
      groupIndexByKey.set(key, groups.length);
      groups.push({ category, name: getEnglishName(item.name), variants: [] });
    }
    groups[groupIndexByKey.get(key)].variants.push(item);
  });
  // Smaller pack sizes first where possible, otherwise keep insertion order.
  groups.forEach((g) => {
    g.variants.sort((a, b) => {
      const na = parseFloat(a.quantity) || 0;
      const nb = parseFloat(b.quantity) || 0;
      return na - nb;
    });
  });

  return (
    <div className="aia-panel">
      <h2 className="aia-panel__title">Quick price &amp; stock update</h2>
      <p className="aia-panel__hint">Change a price or stock count and it saves on its own — no button to press. Items with more than one pack size (e.g. 1kg and 500 grms) are grouped into one row.</p>
      <div className="aia-table-wrap">
        <table className="aia-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Category</th>
              <th>Price (₹) &amp; stock by pack size</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <ItemGroupRow key={`${g.category}::${g.name}`} category={g.category} name={g.name} variants={g.variants} onSave={onSave} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminItemsList() {
  const [itemsByCategory, setItemsByCategory] = useState({ vegetables: [], flowers: [] });
  const [newItemForms, setNewItemForms] = useState({ vegetables: { ...emptyForm }, flowers: { ...emptyForm } });
  const [adding, setAdding] = useState({ vegetables: false, flowers: false });
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all"); // "all" | one of CATEGORIES

  const navigate = useNavigate();

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const results = await Promise.all(CATEGORIES.map(cat => axios.get(`${API_URL}/admin/items/${cat}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
      })));
      const next = {};
      CATEGORIES.forEach((cat, i) => {
        const list = results[i]?.data?.items;
        const arr = Array.isArray(list) ? list : [];
        // If two rows in the same category ever ended up with the same id,
        // React would silently render only one of them (since both would
        // share a key) and the other would just seem to vanish. Guard
        // against that and log it clearly instead of a silent drop.
        const seenIds = new Set();
        const deduped = [];
        arr.forEach((item) => {
          if (seenIds.has(item.id)) {
            console.warn(`Duplicate item id ${item.id} found in "${cat}" - one copy is being hidden. This points to a data problem in the database, not a display bug.`, item);
            return;
          }
          seenIds.add(item.id);
          deduped.push(item);
        });
        next[cat] = deduped;
      });
      setItemsByCategory(next);
    } catch (err) {
      console.error("Failed to load items:", err);
      if (err.response?.status === 401) {
        alert("Your admin session has expired. Please log in again.");
        localStorage.removeItem("adminToken");
        navigate(ADMIN_LOGIN_PATH);
        return;
      }
      alert("Failed to load items");
    }
  };

  // Note: this refreshes the whole list after every save, including
  // auto-saves - it's what keeps the quick-price table and the cards
  // in sync with each other after either one changes something.
  const updateItem = async (category, id, name, price, stock, subcategory, quantity, imageFile) => {
    const payload = new FormData();
    payload.append("name", name);
    payload.append("price", price);
    payload.append("stock", stock);
    payload.append("subcategory", subcategory || "Fresh vegetables");
    payload.append("quantity", quantity || "");

    if (imageFile) payload.append("image", imageFile);

    await axios.put(`${API_URL}/admin/update-item/${category}/${id}`, payload, {
      headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
    });
    await loadItems();
  };

  const deleteItem = async (category, id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API_URL}/admin/delete-item/${category}/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
      });
      loadItems();
    } catch (err) { alert(err.response?.data?.message || "Failed to delete item"); }
  };

  const toggleItem = async (category, id, enabled) => {
    try {
      await axios.put(`${API_URL}/admin/toggle-item/${category}/${id}`, { enabled }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
      });
      loadItems();
    } catch (err) { alert(err.response?.data?.message || "Failed to update availability"); }
  };

  const handleFormChange = (category, field, value) => {
    setNewItemForms(prev => ({ ...prev, [category]: { ...prev[category], [field]: value } }));
  };

  const submitNewItem = async (category, e) => {
    e.preventDefault();
    const form = newItemForms[category];
    if (!form.name || form.price === "" || form.stock === "" || !form.image) return alert("Name, price, stock, and image are all required");

    const payload = new FormData();
    payload.append("name", form.name);
    payload.append("price", form.price);
    payload.append("stock", form.stock);
    payload.append("image", form.image);
    payload.append("subcategory", form.subcategory);
    payload.append("quantity", form.quantity || "");

    setAdding(prev => ({ ...prev, [category]: true }));
    try {
      await axios.post(`${API_URL}/admin/add-item/${category}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
      });
      setNewItemForms(prev => ({ ...prev, [category]: { ...emptyForm } }));
      loadItems();
    } catch (err) { alert(err.response?.data?.message || "Failed to add item"); }
    finally { setAdding(prev => ({ ...prev, [category]: false })); }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    navigate(ADMIN_LOGIN_PATH);
  };

  // Which category sections to render, and which items within them match
  // the search box. Searching looks at the item name and its subcategory.
  const q = searchQuery.trim().toLowerCase();
  const categoriesToShow = activeCategory === "all" ? CATEGORIES : [activeCategory];
  const visibleItemsByCategory = {};
  categoriesToShow.forEach((cat) => {
    const list = itemsByCategory[cat] || [];
    visibleItemsByCategory[cat] = q
      ? list.filter((item) => {
          const name = getEnglishName(item.name).toLowerCase();
          const sub = (item.subcategory || "").toLowerCase();
          return name.includes(q) || sub.includes(q);
        })
      : list;
  });
  const totalVisible = Object.values(visibleItemsByCategory).reduce((sum, list) => sum + list.length, 0);
  const totalAll = Object.values(itemsByCategory).reduce((sum, list) => sum + list.length, 0);
  const filtersActive = q !== "" || activeCategory !== "all";
  const clearFilters = () => {
    setSearchQuery("");
    setActiveCategory("all");
  };

  return (
    <div className="aia-app">
      <style>{styles}</style>

      {/* Sidebar — same nav on every admin page, so switching sections never means hunting for a link */}
      <div className="aia-sidebar">
        <div className="aia-sidebar__brand">Admin Panel</div>
        <nav className="aia-sidebar__nav">
          <button className="aia-sidebar__link" onClick={() => navigate(ADMIN_DASHBOARD_PATH)}>Dashboard</button>
          <button className="aia-sidebar__link aia-sidebar__link--active">Items</button>
          <button className="aia-sidebar__link" onClick={() => navigate(ADMIN_MOBILE_PATH)}>Mobile Accessories</button>
        </nav>
        <div className="aia-sidebar__footer">
          <button className="aia-sidebar__logout" onClick={logout}>Logout</button>
        </div>
      </div>

      {/* Main content */}
      <div className="aia-main">
        <header className="aia-header">
          <h1>Items — Vegetables &amp; Flowers</h1>
          <p>Manage stock, pricing and photos for every product</p>
        </header>

        <div className="aia-content">
          {/* Search + category filter, so a big catalog is quick to narrow down */}
          <div className="aia-toolbar">
            <div className="aia-search">
              <span className="aia-search__icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                className="aia-search__input"
                type="text"
                placeholder="Search items by name or subcategory…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="aia-chip-row">
              <button
                type="button"
                className={`aia-chip ${activeCategory === "all" ? "aia-chip--active" : ""}`}
                onClick={() => setActiveCategory("all")}
              >
                All
              </button>
              {CATEGORIES.map((cat) => (
                <button
                  type="button"
                  key={cat}
                  className={`aia-chip ${activeCategory === cat ? "aia-chip--active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <p className="aia-empty-note" style={{ padding: "0 0 12px" }}>
            Showing {totalVisible} of {totalAll} total items.
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                style={{ marginLeft: 8, background: "none", border: "none", color: "#2874f0", cursor: "pointer", fontWeight: 600, fontSize: 13, padding: 0 }}
              >
                Clear filters
              </button>
            )}
          </p>

          <QuickPriceTable itemsByCategory={itemsByCategory} onSave={updateItem} />

          {q && totalVisible === 0 && (
            <p className="aia-empty-note">No items match "{searchQuery}".</p>
          )}

          {categoriesToShow.map((category) => (
            <div className="aia-section" key={category}>
              <h2 className="aia-section__title">{category}</h2>
              {visibleItemsByCategory[category].length > 0 && (
                <div className="aia-grid">
                  {visibleItemsByCategory[category].map((item) => (
                    <ItemEditCard key={item.id} category={category} item={item} onSave={updateItem} onDelete={deleteItem} onToggle={toggleItem} />
                  ))}
                </div>
              )}

              <form className="aia-add-card" onSubmit={(e) => submitNewItem(category, e)}>
                <p className="aia-add-card__title">Add new {category.slice(0, -1)}</p>
                <input className="aia-input" placeholder="Name" value={newItemForms[category].name} onChange={(e) => handleFormChange(category, "name", e.target.value)} />
                <select className="aia-select" value={newItemForms[category].subcategory} onChange={(e) => handleFormChange(category, "subcategory", e.target.value)}>
                  {SUBCATEGORY_OPTIONS.map((sub) => (<option key={sub} value={sub}>{sub}</option>))}
                </select>
                <input className="aia-input" type="text" placeholder="Quantity (e.g. 500 grms)" value={newItemForms[category].quantity} onChange={(e) => handleFormChange(category, "quantity", e.target.value)} />
                <input className="aia-input" type="number" placeholder="Price" value={newItemForms[category].price} onChange={(e) => handleFormChange(category, "price", e.target.value)} />
                <input className="aia-input" type="number" min="0" placeholder="Stock" value={newItemForms[category].stock} onChange={(e) => handleFormChange(category, "stock", e.target.value)} />
                <input type="file" accept="image/*" onChange={(e) => handleFormChange(category, "image", e.target.files[0])} />
                <button className="aia-btn aia-btn--primary" type="submit" disabled={adding[category]}>
                  {adding[category] ? "Adding…" : `Add ${category.slice(0, -1)}`}
                </button>
              </form>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}